// Palworld TCG price dashboard: one screen that summarises the whole market
// instead of one card at a time. Reads the same daily price board as /prices
// (no new sync, no new tables) and rolls it into market cap, live coverage,
// risers, fallers and rarity averages.
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { getPriceBoard } from "@/lib/prices.functions";
import {
  buildPriceRows,
  buildSealedRows,
  formatMoney,
  TRACKED_CARD_COUNT,
  type PriceRow,
} from "@/lib/prices-shared";
import { RARITY_ORDER } from "@/lib/tcg-cards";
import { CardImage } from "@/components/CardImage";
import { RarityBadge } from "@/components/RarityBadge";
import { CollectionCta } from "@/components/CollectionCta";
import { MarketMovementNote } from "@/components/MarketMovementNote";
import { AffiliateDisclosure, formatUtcStamp } from "@/components/AffiliateDisclosure";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import { breadcrumbSchema, toolSchema } from "@/lib/schema";
import { cardLinkProps } from "@/lib/card-link";

const URL = "https://palstars.com/price-dashboard";
const TITLE = "Palworld TCG Price Dashboard — Market Movers";
const DESCRIPTION =
  "Palworld TCG price dashboard: today's market cap, live listing coverage, biggest 7-day risers and fallers, and average value per rarity across every printed Palworld card.";

const FAQ: FaqItem[] = [
  {
    q: "What is the Palworld TCG price dashboard?",
    a: `It is a single-screen summary of the whole Palworld OFFICIAL CARD GAME market. Instead of looking up one card, it shows the combined value of one copy of every printed card, how much of the board is backed by live marketplace listings today, the biggest seven-day risers and fallers, and the average value of each rarity across all ${TRACKED_CARD_COUNT} tracked cards.`,
  },
  {
    q: "What does Palworld TCG market cap mean here?",
    a: "It is the cost of a one-of-each set: add today's validated market value of a single copy of every printed Palworld card. It is the cleanest single number for whether the set as a whole is rising or falling, because it is not skewed by how many copies of a chase card exist.",
  },
  {
    q: "How do I find which Palworld cards are going up in value?",
    a: "Read the risers table on this page. It ranks every tracked card by seven-day percentage change from stored daily snapshots, so a card only appears once its price has actually moved on the marketplaces — not because a model predicts it.",
  },
  {
    q: "How often does the Palworld price dashboard update?",
    a: "It reads the same daily price board as the Pal Stars price chart, which re-syncs every day at 07:10 UTC from the eBay US and eBay UK APIs. The exact timestamp of the data you are viewing is printed at the top of the page.",
  },
  {
    q: "Can I track my own Palworld cards against this dashboard?",
    a: "Yes. The Pal Stars collection binder lets you log every card you own with the price you paid and the price you sold it for, then shows banked profit, paper profit and ROI against these live market values, and can be shared with a public link.",
  },
];

export const Route = createFileRoute("/price-dashboard")({
  // Server-side render from the real price board so first paint (crawler and
  // browser alike) matches the hydrated client render.
  loader: ({ context }) =>
    context.queryClient.ensureQueryData({
      queryKey: ["price-board"],
      queryFn: () => getPriceBoard(),
      staleTime: 5 * 60 * 1000,
    }),
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Palworld TCG price dashboard — market movers" },
      {
        property: "og:description",
         content: "Market cap, live coverage, 7-day risers and fallers for every Palworld card.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      breadcrumbSchema([
        ["Home", "/"],
        ["Price Chart", "/prices"],
        ["Price Dashboard", "/price-dashboard"],
      ]),
      toolSchema({ name: "Palworld TCG Price Dashboard", description: DESCRIPTION, path: "/price-dashboard" }),
      faqSchema(FAQ, URL),
    ],
  }),
  component: PriceDashboardPage,
});

function Stat({ label, value, note, tone }: { label: string; value: string; note?: string; tone?: number }) {
  return (
    <div className="glass rounded-xl p-4">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p
        className={`mt-1 text-2xl font-extrabold tabular-nums ${
          tone === undefined ? "" : tone >= 0 ? "text-emerald-400" : "text-destructive"
        }`}
      >
        {value}
      </p>
      {note && <p className="mt-1 text-xs text-muted-foreground">{note}</p>}
    </div>
  );
}

function MoverTable({ rows, heading, up }: { rows: PriceRow[]; heading: string; up: boolean }) {
  return (
    <div className="glass rounded-2xl p-4">
      <h3 className="flex items-center gap-2 font-bold">
        {up ? <ArrowUpRight className="h-4 w-4 text-emerald-400" /> : <ArrowDownRight className="h-4 w-4 text-destructive" />}
        {heading}
      </h3>
      {rows.length === 0 ? (
        <p className="mt-2 text-sm text-muted-foreground">No recorded movement yet — snapshots build day by day.</p>
      ) : (
        <ul className="mt-3 space-y-2">
          {rows.map((r) => (
            <li key={r.code} className="flex items-center gap-2 text-sm">
              <CardImage src={r.image} alt="" width={26} height={36} className="rounded" />
              <Link {...cardLinkProps(r.code)} className="min-w-0 flex-1 truncate hover:underline">
                <span className="font-semibold">{r.name}</span>{" "}
                <span className="font-mono text-xs text-muted-foreground">{r.code}</span>
              </Link>
              <RarityBadge code={r.rarity} />
              <span className="tabular-nums">{formatMoney(r.market, "USD")}</span>
              <span
                className={`w-16 text-right font-semibold tabular-nums ${r.change7d >= 0 ? "text-emerald-400" : "text-destructive"}`}
              >
                {r.change7d >= 0 ? "+" : ""}
                {r.change7d.toFixed(1)}%
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PriceDashboardPage() {
  const { data } = useQuery({
    queryKey: ["price-board"],
    queryFn: () => getPriceBoard(),
    refetchOnMount: "always",
    refetchInterval: 15 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
  });

  const rows = useMemo(() => data?.rows ?? [], [data?.rows]);
  const sealed = useMemo(() => buildSealedRows(data?.sealed ?? []), [data?.sealed]);

  const summary = useMemo(() => {
    const marketCap = rows.reduce((sum, r) => sum + r.market, 0);
    const live = rows.filter((r) => r.liveSources.length > 0);
    const sorted = rows.map((r) => r.market).sort((a, b) => a - b);
    const median = sorted.length ? sorted[Math.floor(sorted.length / 2)]! : 0;
    const moved = rows.filter((r) => Math.abs(r.change7d) > 0.01);
    const capChange = moved.length
      ? moved.reduce((sum, r) => sum + r.change7d, 0) / moved.length
      : 0;
    return {
      marketCap,
      median,
      liveShare: rows.length ? live.length / rows.length : 0,
      liveCount: live.length,
      listings: rows.reduce((sum, r) => sum + r.listings, 0),
      capChange,
      movedCount: moved.length,
    };
  }, [rows]);

  const risers = useMemo(
    () => rows.filter((r) => r.change7d > 0).sort((a, b) => b.change7d - a.change7d).slice(0, 8),
    [rows],
  );
  const fallers = useMemo(
    () => rows.filter((r) => r.change7d < 0).sort((a, b) => a.change7d - b.change7d).slice(0, 8),
    [rows],
  );

  const topValue = useMemo(() => rows.slice().sort((a, b) => b.market - a.market).slice(0, 10), [rows]);

  const byRarity = useMemo(() => {
    const map = new Map<string, { rarity: string; count: number; total: number; live: number }>();
    for (const r of rows) {
      const cur = map.get(r.rarity) ?? { rarity: r.rarity, count: 0, total: 0, live: 0 };
      cur.count++;
      cur.total += r.market;
      if (r.liveSources.length > 0) cur.live++;
      map.set(r.rarity, cur);
    }
    return [...map.values()].sort(
      (a, b) => RARITY_ORDER.indexOf(a.rarity as never) - RARITY_ORDER.indexOf(b.rarity as never),
    );
  }, [rows]);

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10 space-y-8">
      <header>
        <p className="text-xs uppercase tracking-widest text-primary">Pal Stars · price dashboard</p>
        <h1 className="mt-2 text-3xl font-extrabold sm:text-4xl">Palworld TCG Price Dashboard</h1>
        <p className="aeo-answer mt-3 max-w-3xl text-muted-foreground">
          The whole Palworld OFFICIAL CARD GAME market on one screen. A one-of-each set of all {rows.length} printed
          cards is worth{" "}
          <strong className="text-foreground tabular-nums">{formatMoney(summary.marketCap, "USD")}</strong> today, with{" "}
           {summary.liveCount} cards backed by live marketplace listings. Prices were updated as of {formatUtcStamp(data?.updated)}.
        </p>
      </header>

      <MarketMovementNote rows={rows} />

      <AffiliateDisclosure updated={data?.updated} />

      <CollectionCta />

      <section>
        <h2 className="text-2xl font-bold">Market pulse</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Stat
            label="Set market cap"
            value={formatMoney(summary.marketCap, "USD")}
            note={`One copy of each of ${rows.length} printed cards`}
          />
          <Stat
            label="Average 7-day move"
            value={`${summary.capChange >= 0 ? "+" : ""}${summary.capChange.toFixed(2)}%`}
            tone={summary.capChange}
            note={`${summary.movedCount} of ${rows.length} cards moved`}
          />
          <Stat
            label="Live listing coverage"
            value={`${Math.round(summary.liveShare * 100)}%`}
            note={`${summary.listings.toLocaleString("en-US")} listings sampled`}
          />
          <Stat
            label="Median card value"
            value={formatMoney(summary.median, "USD")}
             note="Validated English asking prices"
          />
        </div>
      </section>

      <section className="grid gap-4 lg:grid-cols-2">
        <MoverTable rows={risers} heading="Biggest 7-day risers" up />
        <MoverTable rows={fallers} heading="Biggest 7-day fallers" up={false} />
      </section>

      <section className="grid gap-4 lg:grid-cols-2">
        <div className="glass rounded-2xl p-5">
          <h2 className="text-xl font-bold">Most valuable Palworld cards today</h2>
          <ul className="mt-3 space-y-2 text-sm">
            {topValue.map((r) => (
              <li key={r.code} className="flex items-center gap-2">
                <CardImage src={r.image} alt="" width={26} height={36} className="rounded" />
                <Link {...cardLinkProps(r.code)} className="min-w-0 flex-1 truncate hover:underline">
                  <span className="font-semibold">{r.name}</span>{" "}
                  <span className="font-mono text-xs text-muted-foreground">{r.code}</span>
                </Link>
                <RarityBadge code={r.rarity} />
                <span className="font-semibold tabular-nums">{formatMoney(r.market, "USD")}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="glass rounded-2xl p-5">
          <h2 className="text-xl font-bold">Average value per rarity</h2>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <caption className="sr-only">Average Palworld card value by printed rarity</caption>
              <thead className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th scope="col" className="py-1 pr-3">Rarity</th>
                  <th scope="col" className="py-1 pr-3 text-right">Cards</th>
                  <th scope="col" className="py-1 pr-3 text-right">Live</th>
                  <th scope="col" className="py-1 text-right">Average</th>
                </tr>
              </thead>
              <tbody>
                {byRarity.map((r) => (
                  <tr key={r.rarity} className="border-t border-border/40">
                    <td className="py-1.5 pr-3">
                      <RarityBadge code={r.rarity} />
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">{r.count}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">{r.live}</td>
                    <td className="py-1.5 text-right font-semibold tabular-nums">
                      {formatMoney(r.total / Math.max(1, r.count), "USD")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {sealed.length > 0 && (
        <section className="glass rounded-2xl p-5">
          <h2 className="text-2xl font-bold">Sealed product against MSRP</h2>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {sealed.slice(0, 6).map((p) => {
              const over = p.market === null ? null : ((p.market - p.msrp) / p.msrp) * 100;
              return (
                <div key={p.key} className="rounded-xl border border-border/50 p-3">
                  <p className="font-semibold">{p.name}</p>
                  <p className="mt-1 text-lg font-bold tabular-nums">{formatMoney(p.market ?? p.msrp, "USD")}</p>
                  <p className="text-xs text-muted-foreground tabular-nums">
                    MSRP {formatMoney(p.msrp, "USD")}
                    {over === null ? "" : ` · ${over >= 0 ? "+" : ""}${over.toFixed(0)}% vs MSRP`}
                  </p>
                </div>
              );
            })}
          </div>
          <Link to="/marketplace" className="mt-3 inline-block text-sm font-semibold text-primary hover:underline">
            Full sealed & merchandise board →
          </Link>
        </section>
      )}

      <section className="glass rounded-2xl p-5">
        <h2 className="text-xl font-bold">Keep going</h2>
        <ul className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
          <li><Link to="/prices" className="font-semibold text-primary hover:underline">Palworld TCG price chart →</Link> every printed card, source by source.</li>
          <li><Link to="/collection" className="font-semibold text-primary hover:underline">Palworld card collection binder →</Link> track buy price, real sales and profit.</li>
          <li><Link to="/marketplace" className="font-semibold text-primary hover:underline">Palworld TCG marketplace →</Link> sealed product and merch prices.</li>
          <li><Link to="/pack-calculator" className="font-semibold text-primary hover:underline">Palworld pack calculator →</Link> cost per chase card.</li>
        </ul>
      </section>

      <FaqSection items={FAQ} heading="Palworld price dashboard FAQ" />
    </div>
  );
}
